// РЕП.1 + РЕП.3: пошаговый реплей с richer-рендером.
//
// URL: /replay/:matchId. Сервер хранит snapshots[] (см. lib/replay-snapshot.ts).
// Клиент рендерит snapshots[step] — engine на клиенте не нужен.
//
// Возможности:
//   - GameCard-арт для юнитов + рук обеих сторон.
//   - Tap по карте → детали (CardDetailModal-overlay с full art и текстом).
//   - Tap по 🎴 deck / 📥 discard → PileModal-overlay (видны обе стороны).
//   - Tap по Hero → детали Героя с активкой.
//   - Навигация: |◀ ◀ ▶/⏸ ▶ ▶| (шаг) + ▶▶ (фаза) + ⏯ (ход).
//   - SVG-стрелки атак: красная forward, жёлтая counter, оранжевая hero attack.
//   - Сворачиваемая лог-панель (log_text).
//   - Caption над board'ом описывает событие.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ScreenLayout } from '../components/ScreenLayout';
import { HeroAvatar } from '../components/HeroAvatar';
import { GameCard } from '../components/GameCard';
import { getCardData, type CardData } from '../lib/cardData';
import { getToken } from '../api/client';
import { useTr } from '../i18n/useTr';

type Faction = 'deus' | 'syntetics' | 'global' | 'livlife' | 'neutral';

interface ReplayEvent {
  type: 'attack' | 'hero_attack' | 'hero_death' | 'death' | 'redirect'
      | 'parachute' | 'play_action' | 'raschet';
  // attacker/target снэпы — могут отсутствовать для не-attack событий.
  attacker?: { instanceId: number; ownerIdx: 0 | 1; isHero?: boolean };
  target?: { instanceId: number; ownerIdx: 0 | 1; isHero?: boolean };
  atkDmg?: number;
  defDmg?: number;
  ownerIdx?: 0 | 1;
  instanceId?: number;
  fromInstanceId?: number;
  toInstanceId?: number;
  fromOwnerIdx?: 0 | 1;
  toOwnerIdx?: 0 | 1;
}

interface ReplaySnapshot {
  phase: string;
  turn: number;
  firstPlayerIdx: number;
  matchOver: boolean;
  winnerIdx: any;
  players: SnapshotPlayer[];
  pendingPlacements: any[][];
  caption: string;
  t: number;
  phaseEnd?: boolean;
  events?: ReplayEvent[];
}

interface SnapshotPlayer {
  nick: string;
  faction: string;
  hero: any;
  field: any[][];
  hand: any[];
  discard: any[];
  deckSize: number;
  scripts: number;
  influence: number;
  firewalls: number;
  pendingActionId: number | null;
}

interface ReplayResponse {
  id: number;
  mode: string;
  result: string;
  durationSec: number;
  createdAt: string;
  faction: string;
  heroId: string;
  meIdx: 0 | 1;
  replayJson: {
    version: number;
    seed: number;
    snapshots: ReplaySnapshot[];
    durationMs: number;
    winnerIdx: number | null;
    warStakeUsd: number;
    warMultiplier: number;
    warEndReason: string;
    p0: any;
    p1: any;
    inputs: any[];
  } | null;
  logText: string | null;
}

const FACTION_COLOR: Record<string, string> = {
  deus: '#3D7DFF',
  syntetics: '#C93DFF',
  global: '#FF8A1F',
  livlife: '#FF3D3D',
  neutral: '#9CFF3D',
};

const PHASE_LABEL: Record<string, string> = {
  mulligan: 'Мулиган',
  placement: 'Ф2 Размещение',
  reveal: 'Ф3 Раскрытие',
  hero_first: 'Ф4 Атака 1',
  hero_second: 'Ф5 Атака 2',
  end_turn: 'Ф6 Конец',
  draw: 'Ф1 Тяга',
  game_over: 'Конец матча',
  waiting: 'Ожидание',
};

const AUTOPLAY_INTERVAL_MS = 1500;

// Synthetic hero IDs для стрелок: -1=me, -2=enemy. Совпадает с
// match-adapter / anim-replay.
const HERO_ID = { me: -1, enemy: -2 } as const;

export function ReplayScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tr = useTr();
  const [data, setData] = useState<ReplayResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [detailCard, setDetailCard] = useState<CardData | null>(null);
  const [pileView, setPileView] = useState<{ kind: 'deck' | 'discard'; ownerIdx: 0 | 1 } | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    const base = (import.meta as any).env?.VITE_API_URL || '';
    fetch(`${base}/api/replays/${id}`, {
      headers: { 'Authorization': 'Bearer ' + (getToken() || '') }
    })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        return j as ReplayResponse;
      })
      .then((r) => { setData(r); setLoading(false); })
      .catch((e) => { setError(e?.message || 'load failed'); setLoading(false); });
  }, [id]);

  const snapshots: ReplaySnapshot[] = data?.replayJson?.snapshots || [];
  const totalSteps = snapshots.length;
  const currentSnap = snapshots[Math.min(step, totalSteps - 1)] || null;
  const meIdx: 0 | 1 = data?.meIdx ?? 0;
  const enemyIdx: 0 | 1 = (1 - meIdx) as 0 | 1;

  // Auto-play tick.
  useEffect(() => {
    if (!playing || totalSteps === 0) return;
    if (step >= totalSteps - 1) { setPlaying(false); return; }
    const h = setTimeout(() => setStep((s) => Math.min(s + 1, totalSteps - 1)), AUTOPLAY_INTERVAL_MS);
    return () => clearTimeout(h);
  }, [playing, step, totalSteps]);

  // РЕП.3: навигация ▶▶ — следующий phaseEnd snapshot.
  const nextPhaseStep = useMemo(() => {
    for (let i = step + 1; i < totalSteps; i++) {
      if (snapshots[i].phaseEnd) return i;
    }
    return totalSteps - 1;
  }, [step, snapshots, totalSteps]);

  // РЕП.3: навигация ⏯ — следующий снимок где turn увеличился.
  const nextTurnStep = useMemo(() => {
    const curTurn = currentSnap?.turn ?? 1;
    for (let i = step + 1; i < totalSteps; i++) {
      if (snapshots[i].turn > curTurn) return i;
    }
    return totalSteps - 1;
  }, [step, snapshots, totalSteps, currentSnap]);

  if (loading) {
    return (
      <ScreenLayout title="РЕПЛЕЙ" backTo="/archive">
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--t-mute)' }}>
          {tr('Загрузка реплея…', 'Loading replay…')}
        </div>
      </ScreenLayout>
    );
  }
  if (error || !data) {
    return (
      <ScreenLayout title="РЕПЛЕЙ" backTo="/archive">
        <div style={{ padding: 20, color: 'var(--neon-rd)' }}>
          ⚠ {error || tr('реплей не найден', 'replay not found')}
        </div>
      </ScreenLayout>
    );
  }
  if (!data.replayJson || totalSteps === 0) {
    return (
      <ScreenLayout title="РЕПЛЕЙ" backTo="/archive">
        <div style={{ padding: 20, color: 'var(--t-mute)', lineHeight: 1.6 }}>
          {tr(
            'Для этого матча реплей недоступен. Замены реплея — текстовый лог:',
            'No replay available for this match. Text log fallback:'
          )}
          <pre style={{
            marginTop: 10, padding: 10, background: 'var(--bg-0)',
            fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
            color: 'var(--t-md)', whiteSpace: 'pre-wrap', maxHeight: '60vh', overflow: 'auto'
          }}>
            {data.logText || '— пусто —'}
          </pre>
        </div>
      </ScreenLayout>
    );
  }

  const me = currentSnap?.players[meIdx];
  const enemy = currentSnap?.players[enemyIdx];

  const showCardDetail = (cardId: string) => {
    const cd = getCardData(cardId);
    if (cd) setDetailCard(cd);
  };

  const setFieldRef = (key: string) => (el: HTMLDivElement | null) => {
    fieldRefs.current[key] = el;
  };

  return (
    <ScreenLayout title={`РЕПЛЕЙ #${data.id}`} backTo="/archive">
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 6,
        padding: 8, paddingBottom: 110,
      }}>
        {/* Шапка: фаза/ход + log toggle */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '6px 10px', background: 'var(--bg-2)',
          fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
          color: 'var(--t-md)',
          boxShadow: 'inset 0 0 0 0.5px var(--line)',
          clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))',
        }}>
          <span>
            {tr('Ход', 'Turn')} <strong style={{ color: 'var(--neon-li)' }}>{currentSnap?.turn || 1}</strong>
            {' · '}
            <span style={{ color: 'var(--neon-am)' }}>{PHASE_LABEL[currentSnap?.phase || ''] || currentSnap?.phase}</span>
            {currentSnap?.phaseEnd && <span style={{ marginLeft: 6, color: 'var(--neon-li)' }}>● ИТОГ ФАЗЫ</span>}
          </span>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => setLogOpen(true)}
              style={{
                padding: '2px 8px', background: 'transparent',
                border: 0, color: 'var(--neon-cy)',
                fontFamily: 'inherit', fontSize: 10, cursor: 'pointer',
                boxShadow: 'inset 0 0 0 0.5px var(--neon-cy)',
              }}
            >
              📜 {tr('Лог', 'Log')}
            </button>
            <span style={{ color: 'var(--t-mute)' }}>{step + 1}/{totalSteps}</span>
          </span>
        </div>

        {/* Враг */}
        <PlayerBlock
          player={enemy}
          isMe={false}
          ownerIdx={enemyIdx}
          onHeroTap={() => showCardDetail(enemy?.hero?.id || '')}
          onDeckTap={() => setPileView({ kind: 'deck', ownerIdx: enemyIdx })}
          onDiscardTap={() => setPileView({ kind: 'discard', ownerIdx: enemyIdx })}
        />

        {/* Поле + arrows overlay */}
        <FieldOverlayWrapper
          fieldRefs={fieldRefs}
          events={currentSnap?.events || []}
          meIdx={meIdx}
        >
          <FieldRow units={enemy?.field?.[1] || []} faction={enemy?.faction} side="enemy" line={1} lineLabel="2Л" ownerIdx={enemyIdx} onCardTap={showCardDetail} setRef={setFieldRef} />
          <FieldRow units={enemy?.field?.[0] || []} faction={enemy?.faction} side="enemy" line={0} lineLabel="1Л" ownerIdx={enemyIdx} onCardTap={showCardDetail} setRef={setFieldRef} />
          <div style={{ height: 1, background: 'var(--line)', margin: '4px 0' }} />
          <FieldRow units={me?.field?.[0] || []} faction={me?.faction} side="me" line={0} lineLabel="1Л" ownerIdx={meIdx} onCardTap={showCardDetail} setRef={setFieldRef} />
          <FieldRow units={me?.field?.[1] || []} faction={me?.faction} side="me" line={1} lineLabel="2Л" ownerIdx={meIdx} onCardTap={showCardDetail} setRef={setFieldRef} />
        </FieldOverlayWrapper>

        {/* Я */}
        <PlayerBlock
          player={me}
          isMe={true}
          ownerIdx={meIdx}
          onHeroTap={() => showCardDetail(me?.hero?.id || '')}
          onDeckTap={() => setPileView({ kind: 'deck', ownerIdx: meIdx })}
          onDiscardTap={() => setPileView({ kind: 'discard', ownerIdx: meIdx })}
        />

        {/* Hand-bar обеих сторон */}
        <HandBar
          title={tr('Рука соперника', 'Opponent hand')}
          cards={enemy?.hand || []}
          faction={enemy?.faction as Faction}
          dim={true}
          onTap={showCardDetail}
        />
        <HandBar
          title={tr('Моя рука', 'My hand')}
          cards={me?.hand || []}
          faction={me?.faction as Faction}
          dim={false}
          onTap={showCardDetail}
        />

        {/* Caption */}
        <div style={{
          padding: '8px 10px',
          background: currentSnap?.phaseEnd
            ? 'rgba(156, 255, 61, 0.10)'
            : 'rgba(61, 240, 255, 0.08)',
          color: currentSnap?.phaseEnd ? 'var(--neon-li)' : 'var(--neon-cy)',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 11, lineHeight: 1.4, textAlign: 'center',
          boxShadow: 'inset 0 0 0 0.5px ' + (currentSnap?.phaseEnd ? 'var(--neon-li)' : 'var(--neon-cy)'),
          clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))',
          minHeight: 28,
        }}>
          {currentSnap?.caption || '—'}
        </div>
      </div>

      {/* Контролы реплея (fixed внизу) */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'rgba(7, 7, 11, 0.96)',
        boxShadow: 'inset 0 0.5px 0 var(--neon-cy)',
        padding: '8px 12px', display: 'flex',
        flexDirection: 'column', gap: 6,
        zIndex: 100,
      }}>
        <input
          type="range"
          min={0}
          max={Math.max(0, totalSteps - 1)}
          value={step}
          onChange={(e) => { setStep(parseInt(e.target.value, 10)); setPlaying(false); }}
          style={{ width: '100%', accentColor: 'var(--neon-cy)' }}
        />
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <CtrlBtn onClick={() => { setStep(0); setPlaying(false); }} disabled={step === 0} label="|◀" title={tr('к началу', 'to start')} />
          <CtrlBtn onClick={() => { setStep((s) => Math.max(0, s - 1)); setPlaying(false); }} disabled={step === 0} label="◀" title={tr('шаг назад', 'step back')} />
          <CtrlBtn
            onClick={() => setPlaying((p) => !p)}
            disabled={step >= totalSteps - 1 && !playing}
            label={playing ? '⏸' : '▶'}
            title={playing ? tr('пауза', 'pause') : tr('авто', 'auto-play')}
            primary
          />
          <CtrlBtn onClick={() => { setStep((s) => Math.min(totalSteps - 1, s + 1)); setPlaying(false); }} disabled={step >= totalSteps - 1} label="▶" title={tr('шаг вперёд', 'step forward')} />
          <CtrlBtn
            onClick={() => { setStep(nextPhaseStep); setPlaying(false); }}
            disabled={step >= totalSteps - 1}
            label="▶▶"
            title={tr('к концу фазы', 'to phase end')}
            color="var(--neon-li)"
          />
          <CtrlBtn
            onClick={() => { setStep(nextTurnStep); setPlaying(false); }}
            disabled={step >= totalSteps - 1}
            label="⏯"
            title={tr('к следующему ходу', 'next turn')}
            color="var(--neon-am)"
          />
          <CtrlBtn onClick={() => { setStep(totalSteps - 1); setPlaying(false); }} disabled={step >= totalSteps - 1} label="▶|" title={tr('к концу', 'to end')} />
        </div>
      </div>

      {/* CardDetailModal — full art + текст. */}
      {detailCard && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20, zIndex: 200,
          }}
          onClick={() => setDetailCard(null)}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 320, position: 'relative' }}>
            <button
              onClick={() => setDetailCard(null)}
              style={{
                position: 'absolute', top: -28, right: 0,
                background: 'transparent', border: 0, color: 'var(--t-md)',
                fontSize: 24, cursor: 'pointer', padding: 0,
              }}
            >×</button>
            <GameCard card={detailCard} variant="full" />
          </div>
        </div>
      )}

      {/* PileModal — список карт в колоде/сбросе. */}
      {pileView && currentSnap && (() => {
        const owner = pileView.ownerIdx === meIdx ? me : enemy;
        const title = pileView.kind === 'deck'
          ? (pileView.ownerIdx === meIdx ? tr('Моя колода', 'My deck') : tr('Колода врага', 'Enemy deck'))
          : (pileView.ownerIdx === meIdx ? tr('Мой сброс', 'My discard') : tr('Сброс врага', 'Enemy discard'));
        // В реплее по реплей-данным колода тоже может быть видна — но
        // в snapshot.deckSize только число, сам массив скрыт серверной
        // privacy (схема не отдаёт деку). Для сброса — список есть.
        const cards = pileView.kind === 'discard' ? (owner?.discard || []) : null;
        return (
          <PileModalOverlay
            title={title}
            cards={cards}
            deckSize={pileView.kind === 'deck' ? (owner?.deckSize || 0) : null}
            faction={owner?.faction as Faction}
            onClose={() => setPileView(null)}
            onCardTap={showCardDetail}
          />
        );
      })()}

      {/* Log-panel сворачиваемая. */}
      {logOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
            zIndex: 250, display: 'flex', flexDirection: 'column', padding: 12
          }}
          onClick={() => setLogOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-2)', padding: 12, maxHeight: '90vh',
              display: 'flex', flexDirection: 'column', gap: 8,
              boxShadow: 'inset 0 0 0 0.5px var(--neon-cy)',
              clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
            }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontFamily: 'Chakra Petch, sans-serif', color: 'var(--neon-cy)',
              fontSize: 13, fontWeight: 700, letterSpacing: '0.12em'
            }}>
              <span>📜 ЛОГ БОЯ</span>
              <button onClick={() => setLogOpen(false)} style={{
                background: 'transparent', border: 0, color: 'var(--t-md)',
                fontSize: 18, cursor: 'pointer'
              }}>×</button>
            </div>
            <pre style={{
              flex: 1, overflow: 'auto', whiteSpace: 'pre-wrap',
              fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
              color: 'var(--t-md)', lineHeight: 1.5, margin: 0,
              padding: 8, background: 'var(--bg-0)',
            }}>
              {data.logText || '— пусто —'}
            </pre>
          </div>
        </div>
      )}

      <button
        style={{
          position: 'fixed', top: 8, right: 8, padding: '4px 8px',
          background: 'rgba(255,80,80,0.15)', color: 'var(--neon-rd)',
          border: 0, boxShadow: 'inset 0 0 0 0.5px var(--neon-rd)',
          fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
          cursor: 'pointer', zIndex: 99
        }}
        onClick={() => navigate('/archive', { replace: true })}
      >
        ✕ {tr('выход', 'exit')}
      </button>
    </ScreenLayout>
  );
}

function CtrlBtn({ onClick, disabled, label, title, primary, color }: {
  onClick: () => void; disabled?: boolean; label: string; title?: string;
  primary?: boolean; color?: string;
}) {
  const baseColor = color || (primary ? 'var(--neon-cy)' : 'var(--t-md)');
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        flex: 1, maxWidth: 56, minWidth: 36, padding: '7px 0',
        background: primary ? 'rgba(61, 240, 255, 0.15)' : 'rgba(160, 170, 200, 0.08)',
        border: 0,
        boxShadow: `inset 0 0 0 0.5px ${baseColor}`,
        color: disabled ? 'var(--t-mute)' : baseColor,
        fontFamily: 'JetBrains Mono, monospace', fontSize: 12,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        clipPath: 'polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 4px 100%, 0 calc(100% - 4px))',
      }}
    >
      {label}
    </button>
  );
}

function PlayerBlock({ player, isMe, ownerIdx, onHeroTap, onDeckTap, onDiscardTap }: {
  player?: SnapshotPlayer; isMe: boolean; ownerIdx: 0 | 1;
  onHeroTap: () => void;
  onDeckTap: () => void;
  onDiscardTap: () => void;
}) {
  if (!player) return null;
  const color = FACTION_COLOR[player.faction] || 'var(--t-mute)';
  const heroAlive = (player.hero?.hp ?? 0) > 0;
  return (
    <div
      data-instance-id={ownerIdx === 0 ? HERO_ID.me : HERO_ID.enemy}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: 6,
        background: 'var(--bg-2)',
        boxShadow: 'inset 0 0 0 0.5px var(--line)',
        clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))',
      }}
    >
      <div onClick={onHeroTap} style={{ cursor: 'pointer' }}>
        <HeroAvatar
          id={player.hero?.id || ''}
          size={44}
          faction={player.faction as Faction}
          quality="thumb"
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }} onClick={onHeroTap}>
        <div style={{
          fontFamily: 'Chakra Petch, sans-serif', fontSize: 12, fontWeight: 700,
          color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          cursor: 'pointer',
        }}>
          {player.hero?.name_ru || '—'}
          {!heroAlive && <span style={{ marginLeft: 6, color: 'var(--neon-rd)' }}>💀</span>}
        </div>
        <div style={{
          fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
          color: 'var(--t-mute)', display: 'flex', gap: 8, marginTop: 2,
        }}>
          <span>⚔ <strong style={{ color: 'var(--neon-am)' }}>{player.hero?.atk ?? 0}</strong></span>
          <span>🛡 <strong style={{ color: 'var(--neon-cy)' }}>{player.hero?.armor ?? 0}</strong></span>
          <span>❤ <strong style={{ color: heroAlive ? 'var(--neon-li)' : 'var(--neon-rd)' }}>{player.hero?.hp ?? 0}</strong></span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <span>💧 {player.scripts}</span>
            <button
              onClick={onDeckTap}
              style={{
                background: 'transparent', border: 0, color: 'var(--neon-cy)',
                cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 10,
                textDecoration: 'underline', textUnderlineOffset: 2,
              }}
              title={isMe ? 'Открыть колоду' : 'Колода врага (счётчик)'}
            >
              🎴 {player.deckSize}
            </button>
            <button
              onClick={onDiscardTap}
              style={{
                background: 'transparent', border: 0, color: 'var(--neon-am)',
                cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 10,
                textDecoration: 'underline', textUnderlineOffset: 2,
              }}
              title={isMe ? 'Открыть сброс' : 'Сброс врага'}
            >
              📥 {player.discard?.length ?? 0}
            </button>
            <span>✋ {player.hand.length}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ units, faction, lineLabel, line, ownerIdx, onCardTap, setRef }: {
  units: any[]; faction?: string; side: 'me' | 'enemy'; line: 0 | 1;
  lineLabel: string; ownerIdx: 0 | 1;
  onCardTap: (cardId: string) => void;
  setRef: (key: string) => (el: HTMLDivElement | null) => void;
}) {
  const color = FACTION_COLOR[faction || ''] || 'var(--t-mute)';
  const slots = Array.from({ length: 4 }, (_, i) => units[i] || null);
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'stretch' }}>
      <div style={{
        width: 20, fontFamily: 'JetBrains Mono, monospace', fontSize: 8,
        color: 'var(--t-mute)', display: 'flex', alignItems: 'center',
        justifyContent: 'center',
      }}>{lineLabel}</div>
      {slots.map((u, i) => {
        const key = `unit-${ownerIdx}-${line}-${i}`;
        const cardData = u ? getCardData(u.id) : null;
        return (
          <div
            key={i}
            ref={setRef(key)}
            data-instance-id={u?.instanceId}
            data-owner={ownerIdx}
            style={{
              flex: 1, aspectRatio: '0.72', minHeight: 70,
              background: u ? 'var(--bg-0)' : 'transparent',
              border: u ? 'none' : '1px dashed var(--line)',
              opacity: u ? 1 : 0.3,
              padding: 0, position: 'relative',
              cursor: u ? 'pointer' : 'default',
              ...(u && !u.alive ? { filter: 'grayscale(1)', opacity: 0.45 } : {}),
            }}
            onClick={() => u && cardData && onCardTap(u.id)}
          >
            {u && cardData && (
              <div style={{ width: '100%', height: '100%', overflow: 'hidden', boxShadow: `inset 0 0 0 1px ${color}` }}>
                <GameCard
                  card={cardData}
                  variant="compact"
                  style={{ width: '100%', height: '100%', cursor: 'pointer' }}
                />
                {/* live stats overlay (atk/armor/hp может отличаться от base) */}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  display: 'flex', justifyContent: 'space-around',
                  background: 'rgba(7, 7, 11, 0.85)',
                  fontFamily: 'JetBrains Mono, monospace', fontSize: 9,
                  padding: '1px 2px',
                }}>
                  <span style={{ color: u.atk !== cardData.atk ? 'var(--neon-am)' : 'var(--t-md)' }}>{u.atk}</span>
                  <span style={{ color: u.armor !== cardData.armor ? 'var(--neon-cy)' : 'var(--t-md)' }}>{u.armor}</span>
                  <span style={{ color: u.hp > 0 ? (u.hp !== cardData.hp ? 'var(--neon-li)' : 'var(--t-md)') : 'var(--neon-rd)' }}>{u.hp}</span>
                </div>
              </div>
            )}
            {u && !cardData && (
              <div style={{
                width: '100%', height: '100%', padding: 4,
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color,
              }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name_ru}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{u.atk}</span><span>{u.armor}</span><span>{u.hp}</span>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function HandBar({ title, cards, faction, dim, onTap }: {
  title: string; cards: any[]; faction: Faction; dim: boolean;
  onTap: (cardId: string) => void;
}) {
  if (!cards || cards.length === 0) {
    return (
      <div style={{
        padding: '4px 8px', fontFamily: 'JetBrains Mono, monospace',
        fontSize: 9, color: 'var(--t-mute)', letterSpacing: '0.1em',
      }}>
        {title} · {dim ? '?' : '0 карт'}
      </div>
    );
  }
  // Скрытая рука врага — отображаем только face-down плейсхолдеры с
  // количеством. У врага в snapshot.hand[] есть данные только если он
  // показывал их в каком-то контексте (на самом деле match-adapter и
  // сервер скрывают руку, но в replay мы её можем показывать т.к. матч
  // окончен). Пока — рендерим как есть.
  return (
    <div style={{
      padding: '4px 6px', display: 'flex', gap: 4, alignItems: 'center',
      overflowX: 'auto', background: 'var(--bg-2)',
      opacity: dim ? 0.85 : 1,
      boxShadow: 'inset 0 0 0 0.5px var(--line)',
      clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))',
    }}>
      <span style={{
        fontFamily: 'JetBrains Mono, monospace', fontSize: 9,
        color: 'var(--t-mute)', letterSpacing: '0.1em',
        flexShrink: 0, minWidth: 80,
      }}>
        {title}
      </span>
      {cards.map((c) => {
        const cd = getCardData(c.id);
        return (
          <div
            key={c.instanceId}
            onClick={() => cd && onTap(c.id)}
            style={{
              flexShrink: 0, width: 50, aspectRatio: '0.72',
              cursor: cd ? 'pointer' : 'default',
              boxShadow: `inset 0 0 0 1px ${FACTION_COLOR[faction] || 'var(--t-mute)'}`,
            }}
            title={cd?.name_ru || c.name_ru || c.id}
          >
            {cd && <GameCard card={cd} variant="compact" style={{ width: '100%', height: '100%' }} />}
            {!cd && (
              <div style={{
                width: '100%', height: '100%', padding: 2,
                fontFamily: 'JetBrains Mono, monospace', fontSize: 7,
                color: 'var(--t-md)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{c.name_ru || c.id}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// FieldOverlayWrapper — рисует SVG-стрелки атак поверх поля.
function FieldOverlayWrapper({ children, fieldRefs, events, meIdx }: {
  children: React.ReactNode;
  fieldRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  events: ReplayEvent[];
  meIdx: 0 | 1;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [arrows, setArrows] = useState<Array<{
    x1: number; y1: number; x2: number; y2: number; color: string; key: string;
  }>>([]);

  // Перерасчёт стрелок при изменении events или ref-кеша. Дёргается 200мс
  // после рендера снапшота чтобы DOM успел установиться.
  useEffect(() => {
    if (!events || events.length === 0) { setArrows([]); return; }
    const id = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      const cRect = container.getBoundingClientRect();
      const next: typeof arrows = [];
      const flipIdx = (idx: 0 | 1): 0 | 1 => (meIdx === 1 ? (idx === 0 ? 1 : 0) : idx);

      const findCenter = (instanceId: number, ownerIdx: 0 | 1): {x: number; y: number} | null => {
        // Hero — по PlayerBlock (data-instance-id=-1/-2).
        if (instanceId === -1 || instanceId === -2 || ownerIdx == null) {
          // Используем PlayerBlock'и: -1 = me, -2 = enemy.
          // Но в snapshot ownerIdx может быть 0/1 server-side. Для Hero
          // у нас отдельные synthetic ID не используются, используем ownerIdx.
        }
        // Сначала пробуем найти юнит по data-instance-id во всех cells.
        for (const key in fieldRefs.current) {
          const el = fieldRefs.current[key];
          if (!el) continue;
          const idAttr = el.getAttribute('data-instance-id');
          const ownerAttr = el.getAttribute('data-owner');
          if (idAttr === String(instanceId)) {
            const r = el.getBoundingClientRect();
            return { x: r.left + r.width / 2 - cRect.left, y: r.top + r.height / 2 - cRect.top };
          }
        }
        // Hero rect — по PlayerBlock.
        const heroSyntheticId = ownerIdx === meIdx ? -1 : -2;
        const playerEl = container.parentElement?.querySelector(`[data-instance-id="${heroSyntheticId}"]`) as HTMLElement | null;
        if (playerEl) {
          const r = playerEl.getBoundingClientRect();
          return { x: r.left + r.width / 2 - cRect.left, y: r.top + r.height / 2 - cRect.top };
        }
        return null;
      };

      for (let i = 0; i < events.length; i++) {
        const e = events[i];
        // attack: ATK → DEF (red). Counter (DEF → ATK) если есть defDmg > 0
        // или встретится ещё один attack-event с обратными ролями.
        if (e.type === 'attack' && e.attacker && e.target) {
          const a = findCenter(e.attacker.instanceId, flipIdx(e.attacker.ownerIdx));
          const t = findCenter(e.target.instanceId, flipIdx(e.target.ownerIdx));
          if (a && t) {
            next.push({ x1: a.x, y1: a.y, x2: t.x, y2: t.y, color: '#FF3D3D', key: `atk-${i}` });
            // Counter: если target атаковал в ответ → жёлтая стрелка.
            if ((e.defDmg || 0) > 0) {
              next.push({ x1: t.x, y1: t.y, x2: a.x, y2: a.y, color: '#FFB13D', key: `cnt-${i}` });
            }
          }
        } else if (e.type === 'hero_attack' && e.attacker && e.target) {
          const a = findCenter(e.attacker.instanceId, flipIdx(e.attacker.ownerIdx));
          const t = findCenter(e.target.instanceId, flipIdx(e.target.ownerIdx));
          if (a && t) {
            next.push({ x1: a.x, y1: a.y, x2: t.x, y2: t.y, color: '#FF8A1F', key: `ha-${i}` });
          }
        }
      }
      setArrows(next);
    }, 200);
    return () => clearTimeout(id);
  }, [events, meIdx, fieldRefs]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {children}
      </div>
      {arrows.length > 0 && (
        <svg
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            width: '100%', height: '100%',
          }}
        >
          <defs>
            <marker id="arrowhead-red" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 z" fill="#FF3D3D" />
            </marker>
            <marker id="arrowhead-yellow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 z" fill="#FFB13D" />
            </marker>
            <marker id="arrowhead-orange" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 z" fill="#FF8A1F" />
            </marker>
          </defs>
          {arrows.map((a) => {
            const headId = a.color === '#FF3D3D' ? 'arrowhead-red'
                         : a.color === '#FFB13D' ? 'arrowhead-yellow'
                         : 'arrowhead-orange';
            return (
              <line
                key={a.key}
                x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2}
                stroke={a.color}
                strokeWidth={2}
                strokeDasharray="4 2"
                markerEnd={`url(#${headId})`}
                opacity={0.9}
              />
            );
          })}
        </svg>
      )}
    </div>
  );
}

function PileModalOverlay({ title, cards, deckSize, faction, onClose, onCardTap }: {
  title: string;
  cards: any[] | null;     // null = колода (показываем только размер)
  deckSize: number | null; // если cards=null
  faction: Faction;
  onClose: () => void;
  onCardTap: (cardId: string) => void;
}) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 20, zIndex: 220, paddingTop: 60,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 400, maxHeight: '80vh',
          background: 'var(--bg-2)', padding: 12,
          boxShadow: 'inset 0 0 0 0.5px var(--neon-cy)',
          clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: 'var(--neon-cy)', fontFamily: 'Chakra Petch, sans-serif',
          fontSize: 13, fontWeight: 700, letterSpacing: '0.1em'
        }}>
          <span>{title}</span>
          <button onClick={onClose} style={{
            background: 'transparent', border: 0, color: 'var(--t-md)',
            fontSize: 18, cursor: 'pointer'
          }}>×</button>
        </div>
        {cards === null ? (
          <div style={{
            padding: 30, textAlign: 'center', color: 'var(--t-mute)',
            fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
          }}>
            🎴 {deckSize} карт в колоде<br /><br />
            Содержимое колоды скрыто (порядок рандомный).
          </div>
        ) : cards.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--t-mute)' }}>— пусто —</div>
        ) : (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6,
            overflow: 'auto', flex: 1,
          }}>
            {cards.map((c) => {
              const cd = getCardData(c.id);
              return (
                <div
                  key={c.instanceId || c.id}
                  onClick={() => cd && onCardTap(c.id)}
                  style={{
                    aspectRatio: '0.72', cursor: cd ? 'pointer' : 'default',
                    boxShadow: `inset 0 0 0 1px ${FACTION_COLOR[faction] || 'var(--t-mute)'}`,
                  }}
                >
                  {cd && <GameCard card={cd} variant="compact" style={{ width: '100%', height: '100%' }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
